import React, { useState } from 'react';
import { Box, VStack, HStack, SimpleGrid, Text, Button, Heading, Image, useToast } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { supabase } from '../services/supabaseClient';
import { useApp } from '../context/AppContext';

const MotionBox = motion(Box);

const ProductsScreen = ({ onBack }) => {
  const { addProductToCart } = useApp();
  const toast = useToast();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([{ id: 'all', name: 'Todos' }]);

  // Carregar produtos e categorias reais do Supabase
  React.useEffect(() => {
    const loadProductsAndCategories = async () => {
      try {
        setIsLoading(true);
        // Buscar produtos ativos
        const { data: produtos, error: prodError } = await supabase.from('produtos').select('*').eq('ativo', true).order('nome');
        if (prodError) {
          console.error('Erro ao carregar produtos:', prodError);
          toast({
            title: 'Erro ao carregar produtos',
            status: 'error',
            duration: 3000,
            position: 'top',
            isClosable: true
          });
          setProducts([]);
        } else {
          setProducts(produtos || []);
        }

        // Buscar categorias reais
        const { data: cats, error: catError } = await supabase.from('categorias_produtos').select('*').order('nome');
        if (catError) {
          console.error('Erro ao carregar categorias:', catError);
          toast({
            title: 'Erro ao carregar categorias',
            status: 'error',
            duration: 3000,
            position: 'top',
            isClosable: true
          });
        }
        if (cats && cats.length > 0) {
          setCategories([{ id: 'all', name: 'Todos' }, ...cats.map(c => ({ id: c.id, name: c.nome }))]);
        }
      } catch (error) {
        console.error('Erro ao carregar produtos/categorias:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProductsAndCategories();
  }, [toast]);

  // Filtrar produtos por categoria
  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(product => {
        // Se produto.categoria for id (recomendado no futuro), use: return product.categoria === selectedCategory;
        // Por enquanto, comparar nome:
        const cat = categories.find(c => c.id === selectedCategory);
        return cat && (product.categoria === cat.name || product.categoria === cat.id);
      });

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="black"
      overflowY="auto"
      padding={6}
    >
      <VStack spacing={6} align="stretch">
        {/* Header com título e botão voltar */}
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          bg="linear-gradient(180deg, rgba(26,32,44,0.95) 0%, rgba(26,32,44,0.8) 100%)"
          backdropFilter="blur(10px)"
          px={4}
          py={3}
          mb={4}
          borderRadius="xl"
          boxShadow="lg"
        >
          <HStack justifyContent="space-between" alignItems="center" width="100%">
            <Heading size="lg" color="white">🛒 Produtos</Heading>
            <Button
              variant="solid"
              onClick={onBack}
              bg="yellow.400"
              color="gray.800"
              _hover={{ bg: 'yellow.500' }}
              _active={{ bg: 'yellow.600' }}
              size="lg"
              borderRadius="xl"
              px={6}
            >
              Voltar
            </Button>
          </HStack>
        </Box>

        {/* Seletor de Categorias */}
        <Box
          p={6}
          bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          backdropFilter="blur(10px)"
        >
          <HStack mb={4} justify="space-between" align="center">
            <Heading size="md" color="yellow.400" letterSpacing="wide">
              Categorias
            </Heading>
          </HStack>
          
          <Box overflowX="auto" pb={2}>
            <HStack spacing={3}>
              {categories.map((category) => (
                <Button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  variant="solid"
                  bg={selectedCategory === category.id ? "yellow.400" : "whiteAlpha.200"}
                  color={selectedCategory === category.id ? "black" : "white"}
                  _hover={selectedCategory === category.id ? 
                    { bg: 'yellow.500' } : 
                    { bg: 'whiteAlpha.300', transform: 'translateY(-2px)' }
                  }
                  _active={selectedCategory === category.id ? 
                    { bg: 'yellow.600' } : 
                    { bg: 'whiteAlpha.400' }
                  }
                  transition="all 0.2s"
                  height="50px"
                  minWidth="120px"
                  borderRadius="xl"
                  fontSize="lg"
                  fontWeight="bold"
                >
                  {category.name}
                </Button>
              ))}
            </HStack>
          </Box>
        </Box>

        {/* Grid de Produtos */}
        <Box
          p={6}
          bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          backdropFilter="blur(10px)"
        >
          <HStack mb={6} justify="space-between" align="center">
            <Heading size="md" color="yellow.400" letterSpacing="wide">
              Produtos Disponíveis
            </Heading>
          </HStack>
          
          {isLoading ? (
            <VStack spacing={4} align="center" py={8}>
              <Text color="gray.400" fontSize="lg">Carregando produtos...</Text>
            </VStack>
          ) : filteredProducts.length === 0 ? (
            <VStack spacing={4} align="center" py={8}>
              <Text color="gray.400" fontSize="lg">Nenhum produto encontrado</Text>
            </VStack>
          ) : (
            <SimpleGrid columns={2} spacing={4}>
              {filteredProducts.map((product) => (
              <MotionBox
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                bg="whiteAlpha.50"
                _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                borderRadius="xl"
                boxShadow="lg"
                border="1px solid"
                borderColor="whiteAlpha.200"
                overflow="hidden"
              >
              {/* Imagem real do produto */}
              <Box 
                height="180px" 
                bg="gray.700" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                overflow="hidden"
              >
                {product.imagem_url ? (
                  <Image src={product.imagem_url} alt={product.nome} height="100%" width="100%" objectFit="cover" />
                ) : (
                  <Text color="whiteAlpha.700">Sem imagem</Text>
                )}
              </Box>
              
              <Box p={4}>
                <Text fontSize="lg" color="white" fontWeight="bold">{product.nome}</Text>
                <HStack justify="space-between" align="center" mt={2}>
                  <Box 
                    py={1} 
                    px={3} 
                    bg="green.40020"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="green.400"
                  >
                    <Text fontSize="xl" color="green.400" fontWeight="bold">
                      {typeof product.valor === 'number' && !isNaN(product.valor)
                        ? `R$ ${product.valor.toFixed(2).replace('.', ',')}`
                        : 'R$ --'}
                    </Text>
                  </Box>
                </HStack>
                
                <Button 
                  size="lg" 
                  mt={4}
                  width="100%"
                  bg="yellow.400"
                  color="black"
                  _hover={{ bg: "yellow.500", transform: 'translateY(-2px)' }}
                  _active={{ bg: "yellow.600" }}
                  transition="all 0.2s"
                  borderRadius="xl"
                  height="50px"
                  fontSize="lg"
                  fontWeight="bold"
                  onClick={() => {
                    addProductToCart(product);
                    toast({ 
                      title: 'Produto adicionado!', 
                      status: 'success', 
                      duration: 1500,
                      position: 'top',
                      isClosable: true
                    });
                  }}
                >
                  Adicionar
                </Button>
              </Box>
            </MotionBox>
          ))}
          </SimpleGrid>
          )}
        </Box>
      </VStack>
    </MotionBox>
  );
};

export default ProductsScreen;
